import { useState, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import { 
  Scale, 
  Calculator, 
  Briefcase, 
  FileCheck, 
  Microscope, 
  MapPin, 
  Phone, 
  Mail,
  Menu,
  X,
  ChevronRight,
  Quote,
  Shield,
  Award,
  Heart,
  Zap,
  ArrowRight
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.8 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Inicio", href: "#hero" },
    { name: "Nosotros", href: "#nosotros" },
    { name: "Pilares", href: "#diferencia" },
    { name: "Servicios", href: "#servicios" },
    { name: "Testimonios", href: "#testimonios" },
    { name: "Contacto", href: "#contacto" }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "glass border-b border-[#404040]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.a 
              href="#hero"
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ferrer-landing/artifacts/gp78b2r8_Ferrer%20Vega%20PNG.png" 
                alt="Ferrer Vega Corporativo"
                className="h-12 md:h-14 w-auto brightness-0 invert"
              />
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="nav-link text-sm font-medium uppercase tracking-wider"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-[#BFBFBF]"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#262626] md:hidden"
          >
            <div className="flex flex-col items-center justify-center h-full space-y-8">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setIsOpen(false)}
                  className="text-2xl font-medium text-[#BFBFBF] hover:text-white transition-colors"
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      {/* Background Image */}
      <div 
        className="hero-bg"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_ferrer-landing/artifacts/lz9qfqcs_ferrervega.jpg')"
        }}
      />
      <div className="hero-overlay" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="max-w-4xl"
        >
          <motion.p
            variants={fadeInUp}
            className="text-white/80 uppercase tracking-[0.3em] text-sm mb-6"
          >
            Servicios Profesionales Integrales
          </motion.p>
          
          <motion.h1
            variants={fadeInUp}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-8 tracking-tight"
          >
            Excelencia Jurídica
            <br />
            <span className="text-white/70">y Contable que</span>
            <br />
            Impulsa su Negocio
          </motion.h1>
          
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-white/80 max-w-2xl mb-10 leading-relaxed"
          >
            Combinamos precisión contable y visión estratégica para ofrecer 
            soluciones preventivas y correctivas que protegen sus intereses.
          </motion.p>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-[#7F7F7F] rounded-full flex justify-center"
        >
          <motion.div className="w-1 h-3 bg-[#BFBFBF] rounded-full mt-2" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="nosotros" className="py-24 md:py-32 bg-[#262626] relative noise-overlay" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={scaleIn}
            className="relative"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/7433919/pexels-photo-7433919.jpeg"
                alt="Profesionales de Ferrer Vega"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-[#7F7F7F] z-[-1]" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p
              variants={fadeInUp}
              className="text-[#7F7F7F] uppercase tracking-[0.3em] text-sm mb-4"
            >
              Sobre Nosotros
            </motion.p>
            
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#BFBFBF] mb-8 tracking-tight"
            >
              Transformamos la
              <br />
              Complejidad en Claridad
            </motion.h2>
            
            <motion.p
              variants={fadeInUp}
              className="text-[#7F7F7F] text-lg leading-relaxed mb-8"
            >
              En Ferrer Vega, somos una firma de servicios profesionales integrales 
              donde la excelencia jurídica se une a la precisión contable y una visión 
              estratégica inquebrantable. Nos dedicamos a ofrecer soluciones preventivas 
              y correctivas, actuando como su aliado técnico indispensable en cada trámite, 
              auditoría o consultoría administrativa.
            </motion.p>

            {/* Mission & Vision */}
            <motion.div
              variants={staggerContainer}
              className="space-y-6"
            >
              <motion.div variants={fadeInUp} className="glass-card p-6">
                <h3 className="text-xl font-semibold text-[#BFBFBF] mb-3">Nuestra Misión</h3>
                <p className="text-[#7F7F7F]">
                  Brindar servicios de consultoría legal, contable y técnica de la más alta 
                  calidad, proporcionando certidumbre y soluciones innovadoras que protejan 
                  los intereses de nuestros clientes.
                </p>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="glass-card p-6">
                <h3 className="text-xl font-semibold text-[#BFBFBF] mb-3">Nuestra Visión</h3>
                <p className="text-[#7F7F7F]">
                  Ser reconocidos para el 2030 como el despacho líder en soluciones integrales 
                  de negocios en la región, distinguiéndose por simplificar procesos legales 
                  y contables complejos.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Scale,
      title: "Bufete Jurídico y Litigio",
      description: "Defensa legal y representación técnica en diversas materias para garantizar el cumplimiento normativo y proteger sus derechos.",
      large: true
    },
    {
      icon: Calculator,
      title: "Contabilidad y Auditoría",
      description: "Gestión profesional de sus finanzas y auditorías profundas para asegurar la salud fiscal y operativa de su organización."
    },
    {
      icon: Briefcase,
      title: "Consultoría en Administración",
      description: "Asesoría estratégica para optimizar su estructura empresarial y mejorar la toma de decisiones."
    },
    {
      icon: FileCheck,
      title: "Apoyo en Trámites Legales",
      description: "Acompañamiento técnico para la obtención eficiente de licencias, permisos y gestiones ante autoridades."
    },
    {
      icon: Microscope,
      title: "Consultoría Científica y Técnica",
      description: "Servicios especializados de alto rigor técnico para proyectos específicos y peritajes, traduciendo lo complejo a soluciones claras."
    }
  ];

  return (
    <section 
      id="servicios" 
      className="py-24 md:py-32 bg-[#1a1a1a] relative"
      data-testid="services-section"
      style={{
        backgroundImage: "linear-gradient(rgba(26, 26, 26, 0.95), rgba(26, 26, 26, 0.95)), url('https://images.pexels.com/photos/6378848/pexels-photo-6378848.jpeg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed"
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-[#7F7F7F] uppercase tracking-[0.3em] text-sm mb-4"
          >
            Nuestros Servicios
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#BFBFBF] tracking-tight"
          >
            Soluciones Integrales
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="bento-grid"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className={`bento-item glass-card card-hover p-8 ${service.large ? "bento-item-large" : ""}`}
              data-testid={`service-card-${index}`}
            >
              <div className="service-icon rounded-sm">
                <service.icon className="text-[#BFBFBF]" size={24} />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-[#BFBFBF] mb-4">
                {service.title}
              </h3>
              <p className="text-[#7F7F7F] leading-relaxed">
                {service.description}
              </p>
              <motion.div 
                className="mt-6 flex items-center text-[#BFBFBF] opacity-0 group-hover:opacity-100 transition-opacity"
                whileHover={{ x: 5 }}
              >
                <span className="text-sm font-medium">Más información</span>
                <ArrowRight size={16} className="ml-2" />
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const pillars = [
    {
      icon: Shield,
      title: "Probidad Jurídica",
      description: "Actuamos bajo estrictos principios éticos, asegurando transparencia absoluta en cada dictamen y proceso legal."
    },
    {
      icon: Award,
      title: "Excelencia Técnica",
      description: "Formación continua en materia contable, científica y administrativa para ofrecer soluciones precisas."
    },
    {
      icon: Heart,
      title: "Lealtad al Cliente",
      description: "Priorizamos la protección de sus intereses, construyendo relaciones de confianza basadas en resultados."
    },
    {
      icon: Zap,
      title: "Agilidad Resolutiva",
      description: "Maximizamos el uso de tecnología y conocimiento para agilizar trámites legales y administrativos."
    }
  ];

  return (
    <section id="diferencia" className="py-24 md:py-32 bg-[#262626]" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p
              variants={fadeInUp}
              className="text-[#7F7F7F] uppercase tracking-[0.3em] text-sm mb-4"
            >
              Los Pilares de nuestro Corporativo
            </motion.p>
            
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#BFBFBF] mb-8 tracking-tight"
            >
              Nuestra Diferencia
            </motion.h2>
            
            <motion.p
              variants={fadeInUp}
              className="text-[#7F7F7F] text-lg leading-relaxed mb-8"
            >
              Lo que nos distingue es nuestra capacidad de integración. En Ferrer Vega, 
              unificamos la visión legal y contable para que su estrategia fiscal sea 
              legalmente impecable y sus operaciones, sólidamente respaldadas.
            </motion.p>

            <motion.p
              variants={fadeInUp}
              className="text-[#7F7F7F] text-lg leading-relaxed"
            >
              Con un equipo experto que habla el lenguaje de la ingeniería, la ciencia 
              y el derecho, le ofrecemos una perspectiva 360° para una administración 
              y defensa más robusta y persuasiva.
            </motion.p>

            {/* Image */}
            <motion.div
              variants={scaleIn}
              className="mt-12"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ferrer-landing/artifacts/xd4wmva3_abogados.jpg"
                alt="Equipo de abogados Ferrer Vega"
                className="w-full aspect-video object-cover opacity-70"
              />
            </motion.div>
          </motion.div>

          {/* Pillars */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="space-y-6"
          >
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="pillar-card"
                data-testid={`pillar-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#404040] rounded-sm">
                    <pillar.icon className="text-[#BFBFBF]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#BFBFBF] mb-2">
                      {pillar.title}
                    </h3>
                    <p className="text-[#7F7F7F]">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "La auditoría integral de Ferrer Vega nos permitió detectar contingencias fiscales que otros no vieron; su visión combinada legal-contable es simplemente excepcional.",
      author: "Constructora del Bajío",
      role: "Cliente Corporativo"
    },
    {
      quote: "Gracias a su apoyo en trámites legales complejos, pude regularizar mi clínica en tiempo récord; son expertos en navegar la burocracia con eficacia.",
      author: "Dra. Silvia Pantoja",
      role: "Sector Salud"
    },
    {
      quote: "Su consultoría técnica fue clave para nuestro peritaje; hablan el lenguaje de la ingeniería y el derecho con la misma maestría y profesionalismo.",
      author: "Ing. Marco Valerio",
      role: "Consultor Independiente"
    },
    {
      quote: "Delegar nuestra contabilidad y administración en Ferrer Vega ha sido la mejor inversión; su transparencia y reportes detallados nos dan total certidumbre.",
      author: "Inversiones Pro-Norte",
      role: "Grupo de Inversión"
    }
  ];

  return (
    <section id="testimonios" className="py-24 md:py-32 bg-[#1a1a1a]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-[#7F7F7F] uppercase tracking-[0.3em] text-sm mb-4"
          >
            Testimonios
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#BFBFBF] tracking-tight"
          >
            Lo Que Dicen Nuestros Clientes
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={scaleIn}
              className="testimonial-card"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="text-[#7F7F7F] mb-4" size={32} />
              <p className="text-[#BFBFBF] text-lg leading-relaxed mb-6">
                "{testimonial.quote}"
              </p>
              <div className="border-t border-[#7F7F7F]/30 pt-4">
                <p className="text-[#BFBFBF] font-semibold">{testimonial.author}</p>
                <p className="text-[#7F7F7F] text-sm">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    user_phone: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await emailjs.sendForm(
        "service_1sp0lxm",
        "template_ymk6rka",
        formRef.current,
        {
          publicKey: "t41rceK8EbSiIbtr0"
        }
      );

      if (result.status === 200) {
        toast.success("¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.");
        setFormData({
          user_name: "",
          user_email: "",
          user_phone: "",
          message: ""
        });
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      toast.error("Error al enviar el mensaje. Por favor, intente nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Dirección",
      content: "San Juan de Letrán 4341 Int. 8, Residencial Cordilleras CP. 45039, Zapopan, Jalisco, México"
    },
    {
      icon: Phone,
      title: "Teléfono",
      content: "33 2035 8748"
    },
    {
      icon: Mail,
      title: "Email",
      content: "contacto@ferrervega.com"
    }
  ];

  return (
    <section id="contacto" className="py-24 md:py-32 bg-[#262626]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-[#7F7F7F] uppercase tracking-[0.3em] text-sm mb-4"
          >
            Contacto
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#BFBFBF] tracking-tight mb-6"
          >
            ¿Listo para Optimizar su Negocio?
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-[#7F7F7F] text-lg max-w-2xl mx-auto"
          >
            Transforme sus desafíos legales y contables en oportunidades. Contáctenos hoy mismo.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="space-y-6"
              data-testid="contact-form"
            >
              <div>
                <label htmlFor="user_name" className="block text-[#BFBFBF] text-sm font-medium mb-2">
                  Nombre Completo
                </label>
                <Input
                  type="text"
                  id="user_name"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleChange}
                  required
                  placeholder="Su nombre"
                  className="form-input rounded-none bg-[#262626] border-[#7F7F7F] text-[#BFBFBF] placeholder:text-[#7F7F7F] focus:border-[#BFBFBF] focus:ring-[#BFBFBF]"
                  data-testid="input-name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="user_email" className="block text-[#BFBFBF] text-sm font-medium mb-2">
                    Correo Electrónico
                  </label>
                  <Input
                    type="email"
                    id="user_email"
                    name="user_email"
                    value={formData.user_email}
                    onChange={handleChange}
                    required
                    placeholder="correo@ejemplo.com"
                    className="form-input rounded-none bg-[#262626] border-[#7F7F7F] text-[#BFBFBF] placeholder:text-[#7F7F7F] focus:border-[#BFBFBF] focus:ring-[#BFBFBF]"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label htmlFor="user_phone" className="block text-[#BFBFBF] text-sm font-medium mb-2">
                    Teléfono
                  </label>
                  <Input
                    type="tel"
                    id="user_phone"
                    name="user_phone"
                    value={formData.user_phone}
                    onChange={handleChange}
                    placeholder="(000) 000-0000"
                    className="form-input rounded-none bg-[#262626] border-[#7F7F7F] text-[#BFBFBF] placeholder:text-[#7F7F7F] focus:border-[#BFBFBF] focus:ring-[#BFBFBF]"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-[#BFBFBF] text-sm font-medium mb-2">
                  Mensaje
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="¿En qué podemos ayudarle?"
                  className="form-input rounded-none bg-[#262626] border-[#7F7F7F] text-[#BFBFBF] placeholder:text-[#7F7F7F] focus:border-[#BFBFBF] focus:ring-[#BFBFBF] resize-none"
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-6 text-base rounded-none disabled:opacity-50"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <span className="loading-spinner mr-2" />
                    Enviando...
                  </span>
                ) : (
                  <>
                    Enviar Mensaje
                    <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <div className="glass-card p-8">
              <h3 className="text-2xl font-semibold text-[#BFBFBF] mb-8">
                Información de Contacto
              </h3>
              
              <div className="space-y-2">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="contact-info-item"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="p-3 bg-[#404040] rounded-sm">
                      <info.icon className="text-[#BFBFBF]" size={20} />
                    </div>
                    <div>
                      <p className="text-[#7F7F7F] text-sm mb-1">{info.title}</p>
                      <p className="text-[#BFBFBF]">{info.content}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Google Maps */}
              <div className="mt-8 aspect-video w-full overflow-hidden border border-[#7F7F7F]/30">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3732.993700140098!2d-103.42014152529502!3d20.669835300010607!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8428ae9acee738fb%3A0x187eaaa3a1f156eb!2sSan%20Juan%20de%20Letr%C3%A1n%204341-Int.%208%2C%20Residencial%20Cordilleras%2C%2045039%20Zapopan%2C%20Jal.!5e0!3m2!1ses!2smx!4v1775870445645!5m2!1ses!2smx"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación de Ferrer Vega"
                  data-testid="google-map"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 bg-[#1a1a1a] border-t border-[#404040]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          {/* Logo */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_ferrer-landing/artifacts/gp78b2r8_Ferrer%20Vega%20PNG.png" 
              alt="Ferrer Vega Corporativo"
              className="h-16 w-auto brightness-0 invert"
            />
            <p className="text-[#7F7F7F] text-sm mt-3">
              Servicios Profesionales Integrales
            </p>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center gap-6">
            <a href="#nosotros" className="footer-link text-sm">Nosotros</a>
            <a href="#servicios" className="footer-link text-sm">Servicios</a>
            <a href="#testimonios" className="footer-link text-sm">Testimonios</a>
            <a href="#contacto" className="footer-link text-sm">Contacto</a>
          </div>

          {/* Copyright */}
          <div className="text-right">
            <p className="text-[#7F7F7F] text-sm">
              © {new Date().getFullYear()} Ferrer Vega Corporativo.
            </p>
            <p className="text-[#7F7F7F] text-sm">
              Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-[#262626] min-h-screen">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#404040',
            color: '#BFBFBF',
            border: '1px solid #7F7F7F'
          }
        }}
      />
      <Navbar />
      <main>
        <HeroSection />
        <AboutSection />
        <WhyChooseUsSection />
        <ServicesSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
